/**
 * Reusable PDF pop-out viewer for the Doserne portal.
 *
 * Fetches the PDF as a blob (via the same-origin inline proxy, with optional
 * admin auth) and shows it in an <iframe> so the browser renders it inline
 * instead of force-downloading the raw Cloudinary file. A Download button is
 * always available (e.g. for printing).
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, X, Download, FileText } from "lucide-react";

export default function PdfViewerModal({ url, downloadUrl, authHeader, filename = "contract.pdf", title = "PDF", labels = {}, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let active = true;
    let created = null;
    (async () => {
      try {
        const cfg = { responseType: "blob", ...(authHeader ? authHeader() : {}) };
        const res = await axios.get(url, cfg);
        created = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        if (active) setBlobUrl(created);
      } catch {
        if (active) setErr(true);
      }
    })();
    return () => { active = false; if (created) URL.revokeObjectURL(created); };
  }, [url, authHeader]);

  const doDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const L = { download: "Descarcă / Print", close: "Închide", err: "Nu s-a putut încărca PDF-ul", ...labels };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex flex-col" onClick={onClose} data-testid="pdf-viewer-modal">
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-slate-200 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        <h4 className="text-sm font-black text-slate-900 flex items-center gap-2 truncate">
          <FileText className="w-4 h-4 text-sky-600" /> {title}
        </h4>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={doDownload} disabled={!blobUrl}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-black disabled:bg-slate-300"
            data-testid="pdf-viewer-download">
            <Download className="w-4 h-4" /> {L.download}
          </button>
          <button onClick={onClose} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-bold" data-testid="pdf-viewer-close">
            <X className="w-4 h-4" /> {L.close}
          </button>
        </div>
      </div>
      <div className="flex-1 bg-slate-100" onClick={(e) => e.stopPropagation()}>
        {err ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-6">
            <FileText className="w-10 h-10 text-slate-300 mb-3" />
            <p className="text-slate-600 font-semibold text-sm">{L.err}</p>
            {downloadUrl && (
              <a href={downloadUrl} target="_blank" rel="noopener noreferrer" className="mt-3 text-sky-600 underline text-sm font-bold">{L.download}</a>
            )}
          </div>
        ) : !blobUrl ? (
          <div className="h-full flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
        ) : (
          <iframe title="pdf" src={blobUrl} className="w-full h-full" data-testid="pdf-viewer-frame" />
        )}
      </div>
    </div>
  );
}
